/**
 * A streamer's colour, and how the front page knows it without guessing.
 *
 * THE ROOM DECIDES. `apps/rooms/src/lib/channelColor.ts` assigns a hue by a
 * channel's POSITION in the room, not by hashing its name — deliberately,
 * because a hash is stable across sessions right up until two channels in one
 * room collide, and a repeated colour is read as a match. Position guarantees
 * the first ten are all different, which is the case that matters when you
 * are watching six people at once.
 *
 * That rules out a global streamer → colour table, and it also means the
 * front page does not need one. The rule is a pure function of an ordered
 * list, so applying the same rule to the same list reproduces the room's own
 * colours exactly — nothing is stored, and nothing can fall out of sync.
 *
 * The ordered list comes from two places, in this order:
 *
 *   1. `tomo-rooms:room:<id>` in localStorage, which the rooms app writes
 *      with the room's channels as it actually held them. This device has
 *      been in that room, so this is not a prediction — it is what was on
 *      screen. The console and the room are served from ONE origin — `/` and
 *      `/live/` — which is what makes that key readable here at all.
 *   2. The room's `twitch_logins` from the API, for a room you have never
 *      opened, which is the order it will open in.
 */

/** Must match CHANNEL_HUES in apps/rooms/src/lib/channelColor.ts. */
export const CHANNEL_HUES = 10;

/** Just the part of `Storage` this needs, so a test can pass a Map. */
export interface OrderStore {
  read(key: string): string[] | null;
}

/**
 * The hue a channel wears, given the order its room holds it in.
 *
 * Returns null for a channel the room does not contain — the caller has
 * something the room does not, and colouring it would invent a position.
 */
export function hueOf(order: readonly string[], login: string): number | null {
  const index = order.indexOf(login);
  return index < 0 ? null : (index % CHANNEL_HUES) + 1;
}

/** The CSS variable holding that hue, matching the rooms app's `hueVar`. */
export function hueVar(hue: number): string {
  return `var(--color-ch-${String(hue)})`;
}

/**
 * The order a room holds its channels in: what this device saw, else what
 * the API says.
 *
 * A stored list that has drifted from the room's real membership is still
 * the better answer for a card: it is what this person saw last time, and a
 * card exists to be recognised.
 */
export function roomOrder(
  room: { readonly id: string; readonly channels: readonly string[] },
  store: OrderStore,
): readonly string[] {
  const stored = store.read(`tomo-rooms:room:${room.id}`);
  return stored !== null && stored.length > 0 ? stored : room.channels;
}

/**
 * The letter on a channel's marker.
 *
 * One character, like a line letter on a transit map. Takes the first letter
 * or digit rather than the first character, because a login may begin with an
 * underscore and a marker reading "_" identifies nobody.
 */
export function markerLetter(login: string): string {
  const found = /[a-z0-9]/i.exec(login);
  return (found?.[0] ?? '?').toUpperCase();
}

/** Reads the rooms app's own localStorage keys. Same origin, same keys. */
export const browserOrders: OrderStore = {
  read(key) {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')
        ? parsed
        : null;
    } catch {
      // Private mode, blocked storage, or something else wrote this key.
      return null;
    }
  },
};
